import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";

interface User {
  _id: string;
  name: string;
  email: string;
  roles: string[];
  status: string;
  createdAt: string;
}

interface Supplier {
  _id: string;
  name: string;
  email: string;
  category: string;
  status: string;
  createdAt: string;
}

interface UserSupplierListProps {
  users: User[];
  suppliers: Supplier[];
}

const UserSupplierList: React.FC<UserSupplierListProps> = ({
  users,
  suppliers,
}) => {
  const [activeTab, setActiveTab] = useState<"users" | "suppliers">("users");

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "active":
        return (
          <Badge className="bg-green-500 hover:bg-green-600 text-white">
            Active
          </Badge>
        );
      case "inactive":
        return (
          <Badge className="bg-red-500 hover:bg-red-600 text-white">
            Inactive
          </Badge>
        );
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Latest {activeTab === "users" ? "Users" : "Suppliers"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border-b">
          <div className="flex">
            <button
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === "users"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("users")}
            >
              Users
            </button>
            <button
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === "suppliers"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
              onClick={() => setActiveTab("suppliers")}
            >
              Suppliers
            </button>
          </div>
        </div>

        <div className="mt-4">
          {activeTab === "users" ? (
            <div className="space-y-4">
              {users.map((user) => (
                <div
                  key={user._id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <h3 className="font-medium">{user.name}</h3>
                    <p className="text-sm text-gray-500">{user.email}</p>
                    <p className="text-xs text-gray-400">
                      {format(new Date(user.createdAt), "MMM d, yyyy HH:mm")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(user.status)}
                    <Badge variant="outline">{user.roles.join(", ")}</Badge>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {suppliers.map((supplier) => (
                <div
                  key={supplier._id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div>
                    <h3 className="font-medium">{supplier.name}</h3>
                    <p className="text-sm text-gray-500">{supplier.email}</p>
                    <p className="text-xs text-gray-400">
                      {format(
                        new Date(supplier.createdAt),
                        "MMM d, yyyy HH:mm"
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(supplier.status)}
                    <Badge variant="outline">{supplier.category}</Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UserSupplierList;
